import * as React from "react";
import {
  Typography,
  Box,
  Container,
  Button,
  Avatar,
  CircularProgress,
  Chip,
  Tooltip,
  Paper,
  Stack,
  Divider,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { connect } from "react-redux";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import UploadIcon from "@mui/icons-material/Upload";
import ReactFileReader from "react-file-reader";
import {
  centered_flex_box,
  MainInput,
  MainTextArea,
  main_button,
} from "../../app/components/Styles";
import { useNavigate, useParams } from "react-router-dom";
import {
  createWhatsappTemplate,
  updateWhatsappTemplate,
  fetchWhatsappTemplateForEdit,
  uploadWhatsappHeaderMeta,
} from "../../app/store/actions/adminActions";
import { uploadFile } from "../../core/aws";

const LANGUAGE_LABELS = {
  en_US: "English (US)",
  en_GB: "English (UK)",
  fr_FR: "French",
  es_ES: "Spanish",
  ar: "Arabic",
};

function formatLanguageDisplay(code) {
  if (!code) return "";
  return LANGUAGE_LABELS[code] ? `${LANGUAGE_LABELS[code]} (${code})` : code;
}

export const CreateWhatsappTemplate = ({
  isLoading,
  createWhatsappTemplate,
  updateWhatsappTemplate,
  fetchWhatsappTemplateForEdit,
  uploadWhatsappHeaderMeta,
}) => {
  window.Buffer = window.Buffer || require("buffer").Buffer;
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [editLoading, setEditLoading] = React.useState(Boolean(editId));
  const [editLoadError, setEditLoadError] = React.useState(false);
  const [name, setName] = React.useState("");
  const [nameError, setNameError] = React.useState("");
  const [language, setLanguage] = React.useState("en_US");
  /** none | text | image | video */
  const [headerType, setHeaderType] = React.useState("none");
  const [header, setHeader] = React.useState("");
  const [headerMediaHandle, setHeaderMediaHandle] = React.useState("");
  const [headerPublicUrl, setHeaderPublicUrl] = React.useState("");
  const [headerMediaFileLabel, setHeaderMediaFileLabel] = React.useState("");
  const [metaUploading, setMetaUploading] = React.useState(false);
  const [body, setBody] = React.useState("");
  const [footer, setFooter] = React.useState("");
  const [paramLabels, setParamLabels] = React.useState([]);
  const [paramExamples, setParamExamples] = React.useState([]);
  const [paramMappings, setParamMappings] = React.useState([]);
  const [addRsvpButtons, setAddRsvpButtons] = React.useState(true);
  const [rsvpAttendingText, setRsvpAttendingText] = React.useState("Attending");
  const [rsvpNotAttendingText, setRsvpNotAttendingText] = React.useState("Not Attending");
  const [headerError, setHeaderError] = React.useState("");

  const bodyRef = React.useRef(null);

  const countPositionalParams = React.useCallback((text) => {
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    const indices = matches
      .map((m) => Number(m.replace("{{", "").replace("}}", "")))
      .filter((n) => !Number.isNaN(n));
    return indices.length ? Math.max(...indices) : 0;
  }, []);

  const syncParamLabels = React.useCallback(() => {
    const needed = countPositionalParams(body);
    setParamLabels((prev) => {
      const next = [...prev];
      while (next.length < needed) next.push("");
      if (next.length > needed) next.length = needed;
      return next;
    });
    setParamExamples((prev) => {
      const next = [...prev];
      while (next.length < needed) next.push("");
      if (next.length > needed) next.length = needed;
      return next;
    });
    setParamMappings((prev) => {
      const next = [...prev];
      while (next.length < needed) next.push("");
      if (next.length > needed) next.length = needed;
      return next;
    });
  }, [body, countPositionalParams]);

  React.useEffect(() => {
    syncParamLabels();
  }, [body, syncParamLabels]);

  const applyTemplateFromServer = React.useCallback((data) => {
    const { template, graph } = data || {};
    if (!template) return;
    setName(template.name || "");
    setLanguage(template.language || "en_US");
    setAddRsvpButtons(template.isRsvp !== false);
    setRsvpAttendingText(template.rsvpAttendingText || "Attending");
    setRsvpNotAttendingText(template.rsvpNotAttendingText || "Not Attending");
    setHeaderPublicUrl(template.headerPublicUrl || "");
    if (template.headerSampleHandle) setHeaderMediaHandle(template.headerSampleHandle);
    if (template.headerPublicUrl) setHeaderMediaFileLabel("Saved header media");

    const comps = graph?.components || [];
    const findComp = (t) =>
      comps.find((c) => String(c.type).toUpperCase() === String(t).toUpperCase());
    const bodyC = findComp("BODY");
    const bodyText = bodyC?.text || "";

    const foot = findComp("FOOTER");
    setFooter(foot?.text || "");

    const head = findComp("HEADER");
    if (!head) {
      setHeaderType("none");
      setHeader("");
    } else {
      const fmt = String(head.format || "").toUpperCase();
      if (fmt === "TEXT") {
        setHeaderType("text");
        setHeader(head.text || "");
      } else if (fmt === "IMAGE") {
        setHeaderType("image");
        setHeaderMediaHandle(
          head.example?.header_handle?.[0] || template.headerSampleHandle || ""
        );
      } else if (fmt === "VIDEO") {
        setHeaderType("video");
        setHeaderMediaHandle(
          head.example?.header_handle?.[0] || template.headerSampleHandle || ""
        );
      } else {
        setHeaderType("none");
        setHeader("");
      }
    }

    const btn = findComp("BUTTONS");
    if (btn?.buttons?.length >= 2) {
      setAddRsvpButtons(true);
      if (btn.buttons[0]?.text) setRsvpAttendingText(btn.buttons[0].text);
      if (btn.buttons[1]?.text) setRsvpNotAttendingText(btn.buttons[1].text);
    }

    const matches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
    const indices = matches
      .map((m) => Number(m.replace("{{", "").replace("}}", "")))
      .filter((n) => !Number.isNaN(n));
    const n = indices.length ? Math.max(...indices) : 0;
    const row = bodyC?.example?.body_text?.[0] || [];
    const nextExamples = Array.from({ length: n }, (_, i) => row[i] || "");
    const m = template.componentsMapping || {};
    const nextMaps = Array.from(
      { length: n },
      (_, i) => m[String(i + 1)] || m[i + 1] || ""
    );

    setBody(bodyText);
    setTimeout(() => {
      setParamExamples(nextExamples);
      setParamMappings(nextMaps);
    }, 0);
  }, []);

  React.useEffect(() => {
    if (!editId) return;
    setEditLoading(true);
    setEditLoadError(false);
    fetchWhatsappTemplateForEdit(editId)
      .then((data) => {
        applyTemplateFromServer(data);
        setEditLoading(false);
      })
      .catch(() => {
        setEditLoadError(true);
        setEditLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per editId
  }, [editId]);

  const insertVariable = (index) => {
    const placeholder = `{{${index}}}`;
    const el = bodyRef.current;
    if (!el || typeof el.selectionStart !== "number") {
      setBody((prev) => (prev ? `${prev} ${placeholder}` : placeholder));
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd ?? start;
    const next = `${body.slice(0, start)}${placeholder}${body.slice(end)}`;
    setBody(next);
    requestAnimationFrame(() => {
      const pos = start + placeholder.length;
      try {
        el.focus();
        el.selectionStart = pos;
        el.selectionEnd = pos;
      } catch (e) { }
    });
  };

  /** Lowercase, spaces → underscores, strip/replace other invalid chars (matches WhatsApp template name rules). */
  const normalizeTemplateName = React.useCallback((value) => {
    if (value == null) return "";
    let s = String(value).trim().toLowerCase();
    s = s.replace(/\s+/g, "_");
    s = s.replace(/[^a-z0-9_]+/g, "_");
    s = s.replace(/_+/g, "_");
    s = s.replace(/^_+|_+$/g, "");
    if (s.length > 50) s = s.slice(0, 50);
    return s;
  }, []);

  const validateName = React.useCallback((value) => {
    if (!value || !value.trim()) return "Template name is required";
    const normalized = normalizeTemplateName(value);
    const isValid = /^[a-z0-9_]{3,50}$/.test(normalized);
    if (!isValid) return "Use 3-50 chars: lowercase, digits, underscores only";
    return "";
  }, [normalizeTemplateName]);

  const clearHeaderMedia = React.useCallback(() => {
    setHeaderPublicUrl("");
    setHeaderMediaHandle("");
    setHeaderMediaFileLabel("");
    setHeaderError("");
  }, []);

  const handleAddHeaderMedia = async (files) => {
    const file = files?.[0];
    if (!file) return;
    const allowedImage = ["image/jpeg", "image/jpg", "image/png"];
    const allowedVideo = ["video/mp4"];
    const ok =
      headerType === "image"
        ? allowedImage.includes(file.type)
        : headerType === "video"
        ? allowedVideo.includes(file.type)
        : false;
    if (!ok) {
      setHeaderError(headerType === "image" ? "Use JPEG or PNG." : "Use MP4.");
      return;
    }
    setHeaderError("");
    setHeaderMediaFileLabel(file.name);
    setMetaUploading(true);
    try {
      const publicUrl = await uploadFile(file);
      const { assetHandle } = await uploadWhatsappHeaderMeta({
        fileUrl: publicUrl,
        fileName: file.name,
        mimeType: file.type,
      });
      setHeaderPublicUrl(publicUrl);
      setHeaderMediaHandle(assetHandle);
      setHeaderMediaFileLabel(file.name);
    } catch {
      clearHeaderMedia();
    } finally {
      setMetaUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedName = normalizeTemplateName(name);
    setName(normalizedName);
    const nameErr = validateName(normalizedName);
    setNameError(nameErr);
    if (nameErr) return;

    setHeaderError("");
    if (headerType === "text" && !header.trim()) {
      setHeaderError("Enter header text, or set header type to None.");
      return;
    }
    if ((headerType === "image" || headerType === "video") && !headerMediaHandle.trim()) {
      setHeaderError("Upload a sample image/video (JPEG, PNG, or MP4) or paste a Meta asset handle.");
      return;
    }

    const components = [];

    if (headerType === "text" && header.trim()) {
      const text = header.trim();
      components.push({
        type: "HEADER",
        format: "TEXT",
        text,
        example: { header_text: [text] },
      });
    } else if (headerType === "image" && headerMediaHandle.trim()) {
      components.push({
        type: "HEADER",
        format: "IMAGE",
        example: { header_handle: [headerMediaHandle.trim()] },
      });
    } else if (headerType === "video" && headerMediaHandle.trim()) {
      components.push({
        type: "HEADER",
        format: "VIDEO",
        example: { header_handle: [headerMediaHandle.trim()] },
      });
    }

    const numParamsInBody = countPositionalParams(body);
    const exampleRow = (paramExamples.length
      ? paramExamples
      : paramLabels
    ).map((l, i) => l || `param_${i + 1}`);
    // Per Meta docs, body_text is array<array<string>>; example is for placeholders only.
    const bodyComponent = {
      type: "BODY",
      text: body.trim(),
    };
    if (numParamsInBody > 0) {
      bodyComponent.example = { body_text: [exampleRow] };
    }
    components.push(bodyComponent);

    if (footer?.trim()) {
      components.push({ type: "FOOTER", text: footer.trim() });
    }

    if (addRsvpButtons) {
      const yes = (rsvpAttendingText || "Attending").trim().slice(0, 25) || "Attending";
      const no = (rsvpNotAttendingText || "Not Attending").trim().slice(0, 25) || "Not Attending";
      components.push({
        type: "BUTTONS",
        buttons: [
          { type: "QUICK_REPLY", text: yes },
          { type: "QUICK_REPLY", text: no },
        ],
      });
    }

    const componentsMapping = (paramMappings.length ? paramMappings : paramLabels).reduce((acc, label, i) => {
      acc[i + 1] = label || `param_${i + 1}`;
      return acc;
    }, {});

    const headerFormat =
      headerType === "text" || headerType === "image" || headerType === "video"
        ? headerType.toUpperCase()
        : undefined;

    const payload = {
      name: normalizedName,
      language,
      components,
      componentsMapping,
      isRsvp: addRsvpButtons,
      ...(headerFormat ? { headerFormat } : {}),
      ...(headerType === "text" && header.trim() ? { headerText: header.trim() } : {}),
      ...(headerType === "image" || headerType === "video"
        ? {
            headerSampleHandle: headerMediaHandle.trim(),
            ...(headerPublicUrl.trim() ? { headerPublicUrl: headerPublicUrl.trim() } : {}),
          }
        : {}),
      ...(addRsvpButtons
        ? {
            rsvpAttendingText: (rsvpAttendingText || "Attending").trim().slice(0, 25),
            rsvpNotAttendingText: (rsvpNotAttendingText || "Not Attending").trim().slice(0, 25),
          }
        : {}),
    };

    if (editId) {
      updateWhatsappTemplate(editId, payload, navigate);
    } else {
      createWhatsappTemplate(payload, navigate);
    }
  };

  if (isLoading || (editId && editLoading)) {
    return (
      <Box sx={{ ...centered_flex_box, minHeight: "100vh" }}>
        <CircularProgress sx={{ color: "var(--secColor)" }} />
      </Box>
    );
  }

  const numParams = countPositionalParams(body);

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ py: { xs: 3, md: 6 }, mb: { xs: 4, md: 12 }, px: { xs: 2, md: 3 } }}>
        <Box sx={{ ...centered_flex_box, flexDirection: "column", mb: 3 }}>
          <Avatar sx={{ bgcolor: "var(--secColor)" }}>
            <AddCircleOutlineIcon />
          </Avatar>
          <Typography component="h1" variant="h5" fontWeight="bold" sx={{ mt: 1, color: "var(--primaryPurple)" }}>
            {editId ? "Edit WhatsApp Template" : "Create WhatsApp Template"}
          </Typography>
        </Box>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
          {editLoadError && (
            <Typography color="error" sx={{ mb: 2 }}>
              Could not load this template. Try again or check that your account can access it on Meta.
            </Typography>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <Stack spacing={2}>
              <MainInput
                required
                fullWidth
                id="name"
                label="Template Name (lowercase, underscores)"
                name="name"
                value={name}
                InputProps={editId ? { readOnly: true } : undefined}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError(validateName(e.target.value));
                }}
                onBlur={() => {
                  const normalized = normalizeTemplateName(name);
                  setName(normalized);
                  setNameError(validateName(normalized));
                }}
                error={Boolean(nameError)}
                helperText={
                  nameError || (editId ? "Name is fixed for this template on Meta." : "")
                }
                sx={
                  editId
                    ? {
                        "& .MuiInputBase-input": {
                          cursor: "default",
                          color: "var(--primaryPurple)",
                          WebkitTextFillColor: "var(--primaryPurple)",
                        },
                      }
                    : undefined
                }
              />

              {editId ? (
                <MainInput
                  fullWidth
                  id="language"
                  label="Language"
                  name="languageDisplay"
                  value={formatLanguageDisplay(language)}
                  InputProps={{ readOnly: true }}
                  helperText="Language is fixed for this template on Meta."
                  sx={{
                    "& .MuiInputBase-input": {
                      cursor: "default",
                      color: "var(--primaryPurple)",
                      WebkitTextFillColor: "var(--primaryPurple)",
                    },
                  }}
                />
              ) : (
                <MainInput
                  select
                  fullWidth
                  id="language"
                  label="Language"
                  name="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: { sx: { bgcolor: 'var(--platform-surface)', color: 'var(--primaryPurple)' } },
                    },
                  }}
                >
                  <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="en_US">English (US)</MenuItem>
                  <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="en_GB">English (UK)</MenuItem>
                  <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="fr_FR">French</MenuItem>
                  <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="es_ES">Spanish</MenuItem>
                  <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="ar">Arabic</MenuItem>
                </MainInput>
              )}

              <Divider textAlign="left">
                <Typography variant="subtitle2" sx={{ color: "var(--primaryPurple)", fontWeight: 700 }}>
                  {"Template Components (positional params like {{1}}, {{2}})"}
                </Typography>
              </Divider>

              <MainInput
                select
                fullWidth
                id="headerType"
                label="Header"
                name="headerType"
                value={headerType}
                onChange={(e) => {
                  setHeaderType(e.target.value);
                  setHeaderError("");
                  clearHeaderMedia();
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: { sx: { bgcolor: "var(--platform-surface)", color: "var(--primaryPurple)" } },
                  },
                }}
              >
                <MenuItem sx={{ color: "var(--primaryPurple)" }} value="none">
                  None
                </MenuItem>
                <MenuItem sx={{ color: "var(--primaryPurple)" }} value="text">
                  Text
                </MenuItem>
                <MenuItem sx={{ color: "var(--primaryPurple)" }} value="image">
                  Image (upload + Meta sample)
                </MenuItem>
                <MenuItem sx={{ color: "var(--primaryPurple)" }} value="video">
                  Video (upload + Meta sample)
                </MenuItem>
              </MainInput>

              {headerType === "text" && (
                <MainInput
                  fullWidth
                  id="header"
                  label="Header Text"
                  name="header"
                  value={header}
                  onChange={(e) => setHeader(e.target.value)}
                  error={Boolean(headerError && headerType === "text")}
                  helperText={headerError && headerType === "text" ? headerError : undefined}
                />
              )}

              {(headerType === "image" || headerType === "video") && (
                <Stack spacing={1}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Files are stored in your bucket, then sent to Meta’s{" "}
                    <a
                      href="https://developers.facebook.com/docs/graph-api/guides/upload/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      resumable upload
                    </a>{" "}
                    for template review (
                    <a
                      href="https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/overview#media"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      WhatsApp template media
                    </a>
                    ).
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                    <ReactFileReader
                      multipleFiles={false}
                      handleFiles={handleAddHeaderMedia}
                      fileTypes={headerType === "image" ? [".jpeg", ".jpg", ".png"] : [".mp4"]}
                    >
                      <Button
                        type="button"
                        sx={{ ...main_button }}
                        fullWidth
                        disabled={metaUploading}
                        startIcon={metaUploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                      >
                        {headerType === "image" ? "Upload header image" : "Upload header video"}
                      </Button>
                    </ReactFileReader>
                    {headerMediaFileLabel && (
                      <Chip
                        label={headerMediaFileLabel}
                        onDelete={clearHeaderMedia}
                        title={headerMediaFileLabel}
                        sx={{
                          maxWidth: { xs: "100%", sm: 320 },
                          bgcolor: "#f3f0fa",
                          color: "var(--primaryPurple)",
                          border: "1px solid rgba(95, 53, 184, 0.35)",
                          "& .MuiChip-label": {
                            color: "var(--primaryPurple)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          },
                          "& .MuiChip-deleteIcon": {
                            color: "var(--primaryPurple)",
                            "&:hover": { color: "#4527a0" },
                          },
                        }}
                      />
                    )}
                  </Stack>
                  {headerPublicUrl && (headerType === "image" || headerType === "video") && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: "var(--primaryPurple)", mb: 1, fontWeight: 600 }}>
                        Header media preview
                      </Typography>
                      <Box
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "1px solid #e0e0e0",
                          maxWidth: 480,
                          bgcolor: "#f5f5f5",
                        }}
                      >
                        {headerType === "image" ? (
                          <Box
                            component="img"
                            src={headerPublicUrl}
                            alt="WhatsApp template header sample"
                            sx={{
                              width: "100%",
                              maxHeight: 280,
                              objectFit: "contain",
                              display: "block",
                            }}
                          />
                        ) : (
                          <Box
                            component="video"
                            src={headerPublicUrl}
                            controls
                            playsInline
                            sx={{ width: "100%", maxHeight: 320, display: "block" }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ wordBreak: "break-all", color: "text.secondary", display: "block", mt: 1 }}
                      >
                        Stored URL: {headerPublicUrl}
                      </Typography>
                    </Box>
                  )}
                  {editId && (headerType === "image" || headerType === "video") && !headerPublicUrl && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      No bucket URL on file for this template. Upload new media to store a preview, or Meta still has the sample from the original handle.
                    </Typography>
                  )}
                  <MainInput
                    fullWidth
                    id="headerMediaHandle"
                    label="Meta asset handle (filled after upload, or paste manually)"
                    name="headerMediaHandle"
                    value={headerMediaHandle}
                    onChange={(e) => {
                      setHeaderMediaHandle(e.target.value);
                      if (!e.target.value.trim()) {
                        setHeaderPublicUrl("");
                        setHeaderMediaFileLabel("");
                      }
                    }}
                    error={Boolean(headerError && (headerType === "image" || headerType === "video"))}
                    helperText={
                      headerError && (headerType === "image" || headerType === "video")
                        ? headerError
                        : "Requires META_APP_ID and WA_MESSAGING_TOKEN on the server."
                    }
                  />
                </Stack>
              )}

              <Stack spacing={1}>
                <MainTextArea
                  required
                  fullWidth
                  multiline
                  minRows={3}
                  name="body"
                  label="Body (use {{1}}, {{2}} ...)"
                  id="body"
                  value={body}
                  inputRef={bodyRef}
                  onChange={(e) => setBody(e.target.value)}
                />
                <Stack direction="row" spacing={1}>
                  {Array.from({ length: Math.max(1, numParams + 1) }).map((_, i) => (
                    <Button key={i + 1} variant="outlined" size="small" onClick={() => insertVariable(i + 1)}>
                      Insert Variable {i + 1}
                    </Button>
                  ))}
                  {numParams > 0 && (
                    <Button variant="outlined" color="error" size="small" onClick={() => {
                      // Remove the highest-index variable occurrence nearest to caret if present, otherwise all
                      const el = bodyRef.current;
                      if (el && typeof el.selectionStart === 'number') {
                        const caret = el.selectionStart;
                        const pattern = /\{\{(\d+)\}\}/g;
                        let match; let foundIndex = -1; let foundLen = 0;
                        while ((match = pattern.exec(body)) !== null) {
                          const start = match.index; const end = start + match[0].length;
                          if (start <= caret && caret <= end) { foundIndex = start; foundLen = match[0].length; break; }
                        }
                        if (foundIndex !== -1) {
                          const next = body.slice(0, foundIndex) + body.slice(foundIndex + foundLen);
                          setBody(next);
                          requestAnimationFrame(() => {
                            const pos = Math.max(0, foundIndex);
                            try { el.focus(); el.selectionStart = pos; el.selectionEnd = pos; } catch (e) { }
                          });
                          return;
                        }
                      }
                      // Fallback: remove last variable occurrence
                      const next = body.replace(/(.*)\{\{\d+\}\}(.*)$/s, (m, a, b) => a + b);
                      setBody(next);
                    }}>
                      Remove Variable
                    </Button>
                  )}
                </Stack>
              </Stack>

              {numParams > 0 && (
                <Stack spacing={1}>
                  <Divider textAlign="left">
                    <Typography variant="subtitle2" sx={{ color: "var(--primaryPurple)", fontWeight: 700 }}>
                      Parameter Examples and Mappings
                    </Typography>
                  </Divider>
                  {Array.from({ length: numParams }).map((_, i) => (
                    <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <MainInput
                        fullWidth
                        id={`example_${i + 1}`}
                        label={`Example for {{${i + 1}}}`}
                        name={`example_${i + 1}`}
                        value={paramExamples[i] || ""}
                        onChange={(e) => {
                          const next = [...paramExamples];
                          next[i] = e.target.value;
                          setParamExamples(next);
                        }}
                      />
                      <MainInput
                        select
                        fullWidth
                        id={`mapping_${i + 1}`}
                        label={`Mapping for {{${i + 1}}}`}
                        name={`mapping_${i + 1}`}
                        value={(paramMappings[i] === 'name' || paramMappings[i] === 'phoneNumber') ? paramMappings[i] : ''}
                        onChange={(e) => {
                          const next = [...paramMappings];
                          next[i] = e.target.value;
                          setParamMappings(next);
                        }}
                        SelectProps={{
                          displayEmpty: true,
                          MenuProps: {
                            PaperProps: { sx: { bgcolor: 'var(--platform-surface)', color: 'var(--primaryPurple)' } },
                          },
                        }}
                      >
                        <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="">
                          <em>Select mapping</em>
                        </MenuItem>
                        <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="name">name</MenuItem>
                        <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="phoneNumber">phoneNumber</MenuItem>
                      </MainInput>
                    </Stack>
                  ))}
                  <Tooltip title="Examples populate WhatsApp preview; mappings define {{n}} -> field.">
                    <Chip label={`${numParams} parameter${numParams > 1 ? "s" : ""} detected`} />
                  </Tooltip>
                </Stack>
              )}

              <MainInput
                fullWidth
                id="footer"
                label="Footer Text (Optional)"
                name="footer"
                value={footer}
                onChange={(e) => setFooter(e.target.value)}
              />

              <Tooltip title="Two quick-reply buttons for RSVP-style responses">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addRsvpButtons}
                      onChange={(e) => setAddRsvpButtons(e.target.checked)}
                      name="addRsvpButtons"
                      id="addRsvpButtons"
                    />
                  }
                  label="Add RSVP quick-reply buttons"
                />
              </Tooltip>

              {addRsvpButtons && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <MainInput
                    fullWidth
                    id="rsvpAttendingText"
                    label="First button text (max 25 chars)"
                    name="rsvpAttendingText"
                    value={rsvpAttendingText}
                    inputProps={{ maxLength: 25 }}
                    onChange={(e) => setRsvpAttendingText(e.target.value)}
                  />
                  <MainInput
                    fullWidth
                    id="rsvpNotAttendingText"
                    label="Second button text (max 25 chars)"
                    name="rsvpNotAttendingText"
                    value={rsvpNotAttendingText}
                    inputProps={{ maxLength: 25 }}
                    onChange={(e) => setRsvpNotAttendingText(e.target.value)}
                  />
                </Stack>
              )}

              <Button type="submit" fullWidth variant="contained" sx={{ ...main_button, mt: 1 }} disabled={Boolean(editId && editLoadError)}>
                {editId ? "Save changes" : "Create Template"}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  token: state?.auth?.token,
  isLoading: state?.events?.isLoading,
});

export default connect(mapStateToProps, {
  createWhatsappTemplate,
  updateWhatsappTemplate,
  fetchWhatsappTemplateForEdit,
  uploadWhatsappHeaderMeta,
})(CreateWhatsappTemplate);

